import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Every bulk batch this user currently has running, wherever it was started
 * from - People Search's bulk unlock, a Workspace column run, another browser
 * tab, or a run from before the last reload.
 *
 * This is the state behind the notification panel's Batch Progress card, and
 * it is deliberately SEPARATE from radarBulkUnlockSlice / workspaceEmailSlice:
 * those track a batch the current screen started and care about which rows it
 * touched, while this one only ever knows the aggregate and must survive the
 * screen that started it being unmounted (the whole point is that a user can
 * kick off 500 contacts, navigate away, and still watch it finish).
 *
 * Two sources feed it, and they cover each other's gaps:
 *  - `GET /api/workflows/bulk` + `/api/phone-workflows/bulk` (see
 *    workflowApi.listBulkWorkflows) on panel open and on a slow poll. This is
 *    the only way to learn about a batch this session never started.
 *  - The `bulk.progress` SSE event, pushed every 5 completions plus once
 *    final. This is what makes the bar move between polls, and for a batch
 *    over 100 items it is the ONLY live signal the backend emits at all
 *    (Workflow-Service suppresses every per-item event above that threshold -
 *    see its SSE_SILENT_ITEM_THRESHOLD).
 */

export type BulkBatchCategory = 'email' | 'phone';

export interface BulkBatchCounts {
  completed: number;
  found: number;
  notFound: number;
  creditExhausted: number;
}

export interface TrackedBulkBatch {
  /** `${category}:${batchId}` - email and phone batch ids come from two different Mongo collections, so neither is unique on its own. */
  key: string;
  batchId: string;
  category: BulkBatchCategory;
  /** Which surface started it, when known - the list endpoint reports it, a `bulk.progress` event does not. */
  source: 'radar' | 'workspace' | 'enrichment' | null;
  total: number;
  counts: BulkBatchCounts;
  /** ISO string from the batch doc; a batch first seen via SSE falls back to when this client saw it. */
  createdAt: string;
  /**
   * When THIS client first learned of the batch. Used to decide whether a
   * batch missing from a list response actually finished, or simply didn't
   * exist yet when that request was sent - see bulkBatchesHydrated.
   */
  firstSeenAt: number;
  /** Finished: the list dropped it, or its final `bulk.progress` arrived. */
  done: boolean;
  /** When it finished, for the short grace period a finished card stays visible. */
  doneAt: number | null;
  /**
   * Whether this is a BACKGROUND run - the only kind the notification panel
   * shows, and the only kind whose completion refreshes the contact lists.
   *
   * An inline run (<=100 contacts) already reports itself: spinners on its
   * rows, a progress pill in the bulk action bar, and its own refresh as it
   * goes. Repeating it in the notification panel is noise for something the
   * user is already watching, and it would make a panel opened during a small
   * unlock look like a queue of background jobs. never faces this
   * because its <=100 path is client-side and creates no batch document at
   * all; ours goes through the same bulk endpoint either way, so the
   * distinction has to be carried here.
   *
   * Set true outright by bulkBatchRegistered, which only runs from the
   * background path. Batches learned from elsewhere (another tab, a poll) are
   * INFERRED from size - see isBackgroundSize for why that inference can't be
   * the only mechanism.
   */
  background: boolean;
}

export interface BulkBatchesState {
  byKey: Record<string, TrackedBulkBatch>;
  /**
   * Batch keys known to be background work before their card exists - see
   * bulkBatchBackgroundClaimed. A batch created later by a list poll or a
   * `bulk.progress` event reads this instead of inferring from its size.
   */
  claimedBackground: Record<string, true>;
}

const initialState: BulkBatchesState = {
  byKey: {},
  claimedBackground: {},
};

/** How long a finished batch's card lingers before it's swept, so a run that completes while the panel is open doesn't just vanish mid-glance. */
export const FINISHED_BATCH_GRACE_MS = 30_000;

/**
 * Above this many contacts a People Search unlock runs in the BACKGROUND
 * instead of inline: no per-row spinners, no progress pill in the action bar,
 * a "submitted" toast, and progress watched from the notification panel.
 *
 * Not a UI preference - it's the backend's own line. Workflow-Service freezes
 * `bulkSilent = accepted > 100` onto every item of a batch this size and then
 * pushes NO per-item SSE event for any of them, not even the terminal
 * `workflow.finished`. The inline experience is built entirely on those events
 * (each names its row via `input.personId`, which is what lets a row reveal
 * itself without a request per contact), so above 100 there is nothing left to
 * drive it. The only live signal is the batch's own aggregate `bulk.progress`,
 * which says how far the whole batch has got and nothing about which row
 * moved - a notification-panel signal, not a per-row one.
 *
 * splits at the same number for the same reason (its
 * BulkActionBarUI.handleUsageModelBulkUnlock: "<=100 contacts: client-side
 * concurrent processing" vs "> 100 contacts: server-side batch deposit",
 * toasting "Track progress from the notification bar"). We deliberately do NOT
 * copy its "Large Bulk Unlock" confirmation modal - the credit guard already
 * gates the spend.
 *
 * Lives in this slice rather than in useBulkContactUnlock because both the
 * hook (which branches on it) and this slice (which classifies batches by it)
 * need it, and the slice is the lower-level of the two.
 */
export const LARGE_BATCH_THRESHOLD = 100;

/**
 * Best-effort classification for a batch this client did NOT start, where the
 * only evidence is the batch document itself.
 *
 * Note `total` is ACCEPTED items, after the service drops duplicates - so a
 * user who selected 150 contacts of which 60 were already running gets a batch
 * whose total is 90, and this returns false for a run that really was a
 * background one. That misread is why bulkBatchRegistered stamps `background`
 * explicitly for locally-started runs rather than leaving it to be inferred:
 * without it, submitting 150 could show a "track it in the notification panel"
 * toast and then no card in the panel.
 */
const isBackgroundSize = (total: number) => total > LARGE_BATCH_THRESHOLD;

const batchKey = (category: BulkBatchCategory, batchId: string) => `${category}:${batchId}`;

const emptyCounts = (): BulkBatchCounts => ({
  completed: 0,
  found: 0,
  notFound: 0,
  creditExhausted: 0,
});

/** One entry of the list endpoints' response - the aggregate batch doc. */
export interface BulkBatchSnapshotInput {
  _id: string;
  source?: string | null;
  total: number;
  counts: BulkBatchCounts;
  createdAt: string;
  completedAt: string | null;
}

const bulkBatchesSlice = createSlice({
  name: 'bulkBatches',
  initialState,
  reducers: {
    /**
     * Applies one category's list response.
     *
     * `requestedAt` is when the fetch was SENT, and it's what makes pruning
     * safe. The list only contains unfinished batches, so a tracked batch
     * missing from it has normally finished - but a batch created after the
     * request went out would also be missing, and marking that one done would
     * freeze a card at 0% for a run that is actually just starting. Comparing
     * against `firstSeenAt` keeps exactly those out of the prune.
     */
    bulkBatchesHydrated(
      state,
      action: PayloadAction<{
        category: BulkBatchCategory;
        batches: BulkBatchSnapshotInput[];
        requestedAt: number;
      }>
    ) {
      const { category, batches, requestedAt } = action.payload;
      const now = Date.now();
      const seen = new Set<string>();

      for (const doc of batches) {
        const key = batchKey(category, doc._id);
        seen.add(key);
        const existing = state.byKey[key];
        const source =
          doc.source === 'radar' || doc.source === 'workspace' || doc.source === 'enrichment'
            ? doc.source
            : null;
        if (existing) {
          existing.total = doc.total;
          // Never walk a counter backwards: a `bulk.progress` event can easily
          // be newer than a poll that was already in flight when it arrived.
          if (doc.counts.completed >= existing.counts.completed) existing.counts = doc.counts;
          existing.source = source ?? existing.source;
          if (doc.completedAt) {
            existing.done = true;
            existing.doneAt = existing.doneAt ?? now;
          }
        } else {
          state.byKey[key] = {
            key,
            batchId: doc._id,
            category,
            source,
            total: doc.total,
            counts: doc.counts ?? emptyCounts(),
            createdAt: doc.createdAt,
            firstSeenAt: now,
            done: doc.completedAt !== null,
            doneAt: doc.completedAt !== null ? now : null,
            background: isBackgroundSize(doc.total) || !!state.claimedBackground[key],
          };
        }
      }

      for (const batch of Object.values(state.byKey)) {
        if (batch.category !== category || batch.done) continue;
        if (seen.has(batch.key)) continue;
        if (batch.firstSeenAt > requestedAt) continue;
        batch.done = true;
        batch.doneAt = now;
        // Absence from the list is the completion signal, but it carries no
        // counters - so snap the bar to full rather than leaving it parked at
        // whatever the last event happened to report.
        batch.counts = { ...batch.counts, completed: batch.total };
      }
    },

    /**
     * Records a batch this session just created, straight from the create
     * response - before any event or poll has mentioned it.
     *
     * Two things depend on this. The panel shows the run immediately rather
     * than up to 20 seconds later, which matters most for exactly the case it
     * exists for: submit 500 contacts, open the bell, expect to see it. And it
     * is the only place `source` is known for certain - a `bulk.progress`
     * event doesn't carry one, so without this a batch that finished before
     * any list poll saw it would have an unknown source (see
     * bulk-batch-completion.utils' shouldRefreshContacts).
     */
    bulkBatchRegistered(
      state,
      action: PayloadAction<{
        batchId: string;
        category: BulkBatchCategory;
        source: TrackedBulkBatch['source'];
        total: number;
      }>
    ) {
      const { batchId, category, source, total } = action.payload;
      const key = batchKey(category, batchId);
      const now = Date.now();
      const existing = state.byKey[key];
      if (existing) {
        // Events routinely beat the create response back (the service enqueues
        // a batch's jobs before it replies), so this must not reset progress.
        // `background` is only ever promoted to true: a size-based inference
        // that already ran must not override what the submitting path knows.
        existing.source = source ?? existing.source;
        if (total > 0) existing.total = total;
        existing.background = true;
        return;
      }
      state.byKey[key] = {
        key,
        batchId,
        category,
        source,
        total,
        counts: emptyCounts(),
        createdAt: new Date(now).toISOString(),
        firstSeenAt: now,
        done: total === 0,
        doneAt: total === 0 ? now : null,
        // Stated, not inferred - this action only ever runs from the
        // background path, and `total` here is post-dedup so a size check
        // could disagree with the branch that actually chose this path.
        background: true,
      };
    },

    /**
     * Applies one `bulk.progress` SSE event. Upserts, so a batch started in
     * another tab shows up here the moment its first event lands, without
     * waiting for the next poll.
     */
    bulkBatchProgressReceived(
      state,
      action: PayloadAction<{
        batchId: string;
        category: BulkBatchCategory;
        completed: number;
        total: number;
        counts?: Partial<BulkBatchCounts>;
        isFinal: boolean;
      }>
    ) {
      const { batchId, category, completed, total, counts, isFinal } = action.payload;
      const key = batchKey(category, batchId);
      const now = Date.now();
      const existing = state.byKey[key];
      const nextCounts: BulkBatchCounts = {
        ...emptyCounts(),
        ...(existing?.counts ?? {}),
        ...(counts ?? {}),
        completed,
      };

      if (existing) {
        // Events are pushed every 5 completions and can arrive out of order
        // behind a reconnect; an older one must not rewind the bar.
        if (completed >= existing.counts.completed) existing.counts = nextCounts;
        if (total > 0) existing.total = total;
        if (isFinal) {
          existing.done = true;
          existing.doneAt = existing.doneAt ?? now;
          existing.counts = { ...existing.counts, completed: existing.total };
        }
      } else {
        state.byKey[key] = {
          key,
          batchId,
          category,
          source: null,
          total,
          counts: nextCounts,
          createdAt: new Date(now).toISOString(),
          firstSeenAt: now,
          done: isFinal,
          doneAt: isFinal ? now : null,
          // Inferred: an event carries no hint of which path started the run,
          // and a batch first seen here was started somewhere this client
          // can't ask - another tab, or before a reload.
          background: isBackgroundSize(total) || !!state.claimedBackground[key],
        };
      }
    },

    /**
     * Marks a batch as background work without creating its card.
     *
     * For the People All-tab export over 100 contacts: the Search Service
     * creates the Workflow-Service batches itself, one per kind, so this
     * client never sees a create response with their total - and a kind with
     * 100 or fewer to unlock gets a batch that looks inline by size. Their per-contact finish events name the
     * batch but carry no total, so the card is left to whichever of the list
     * poll or `bulk.progress` brings a real one - a card made here would read
     * 0/0, which bulkBatchPercent shows as finished.
     */
    bulkBatchBackgroundClaimed(
      state,
      action: PayloadAction<{ batchId: string; category: BulkBatchCategory }>
    ) {
      const key = batchKey(action.payload.category, action.payload.batchId);
      state.claimedBackground[key] = true;
      const existing = state.byKey[key];
      if (existing) {
        existing.background = true;
        existing.source = existing.source ?? 'radar';
      }
    },

    /** Drops finished cards once their grace period is up. */
    bulkBatchesSwept(state, action: PayloadAction<{ now: number }>) {
      for (const [key, batch] of Object.entries(state.byKey)) {
        if (!batch.done || batch.doneAt === null) continue;
        if (action.payload.now - batch.doneAt < FINISHED_BATCH_GRACE_MS) continue;
        delete state.byKey[key];
        delete state.claimedBackground[key];
      }
    },

    bulkBatchDismissed(state, action: PayloadAction<string>) {
      delete state.byKey[action.payload];
      delete state.claimedBackground[action.payload];
    },
  },
});

/**
 * Newest first, matching the order the list endpoints return.
 *
 * Memoized, and that is not optional here: this builds a new array every time
 * it runs, and `useSelector` compares by reference - an unmemoized version
 * would re-render the panel on EVERY dispatch in the app, which during a bulk
 * run is a great many of them (every per-item workflow event writes to
 * workflowProgress). Keyed off `byKey`, whose identity Immer only changes when
 * this slice actually changes.
 */
export const selectBulkBatches = createSelector(
  (state: { bulkBatches: BulkBatchesState }) => state.bulkBatches.byKey,
  (byKey): TrackedBulkBatch[] =>
    Object.values(byKey)
      // BACKGROUND runs only. An inline unlock already shows itself on the
      // screen that started it - row spinners plus a progress pill in the bulk
      // action bar - so listing it here too would be duplicate reporting, and
      // would turn the panel into a log of every small unlock the user does.
      // See TrackedBulkBatch.background.
      .filter((batch) => batch.background)
      // PEOPLE SEARCH only, for now. The list endpoints return every bulk
      // batch the user has running, Workspace column enrichments included -
      // those reach Workflow-Service's same bulk endpoint via
      // Enrichment-Service's /workspace/sendBulkEmail, with source
      // "workspace". Showing them here would promise tracking this card
      // doesn't yet deliver: nothing feeds a Workspace run's live progress
      // into this slice, so its bar would only move on the 20s poll and its
      // completion would do nothing for the sheet. Dropping the filter is the
      // single change needed if Workspace is wired up later.
      //
      // An unknown source is kept: it means the batch was only ever seen over
      // SSE (started in another tab), and a People Search run is the likelier
      // origin than a Workspace one.
      .filter((batch) => batch.source !== 'workspace' && batch.source !== 'enrichment')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
);

/** Returns a number, so this one is safe to use unmemoized. */
export const selectRunningBulkBatchCount = (state: { bulkBatches: BulkBatchesState }): number => {
  const byKey = state.bulkBatches.byKey;
  let running = 0;
  for (const key of Object.keys(byKey)) {
    const batch = byKey[key];
    // Same two filters the list selector applies - see it for both.
    if (!batch.background || batch.done) continue;
    if (batch.source === 'workspace' || batch.source === 'enrichment') continue;
    running += 1;
  }
  return running;
};

/** Percent complete, clamped - a batch of 0 accepted items reads as finished, not as NaN. */
export function bulkBatchPercent(batch: TrackedBulkBatch): number {
  if (batch.done) return 100;
  if (batch.total <= 0) return 100;
  return Math.max(0, Math.min(100, (batch.counts.completed / batch.total) * 100));
}

export const {
  bulkBatchesHydrated,
  bulkBatchRegistered,
  bulkBatchProgressReceived,
  bulkBatchBackgroundClaimed,
  bulkBatchesSwept,
  bulkBatchDismissed,
} = bulkBatchesSlice.actions;

export default bulkBatchesSlice.reducer;
